import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { usePlan } from "../context/PlanContext";
import { useAuth } from "../context/AuthContext";
import {
  Check, Sparkles, Loader2, ArrowRight, Shield, Clock, Users,
  FileText, BarChart3, Zap, Star, Building2, Calculator, Briefcase,
  ChevronRight, ChevronDown
} from "lucide-react";
import { Button } from "../components/ui/button";
import SEO from "../seo/SEO";
import { useT } from "../seo/i18n";
import { ORGANIZATION_SCHEMA, buildFAQSchema, buildBreadcrumbSchema } from "../seo/seoConfig";

const PRICING_FAQS = [
  { q: "Is there a free plan?", a: "Yes — InvoiceSetu has a forever-free plan with unlimited invoices and quotations, customer master, and Email/WhatsApp share. No credit card required to start." },
  { q: "What does the trial cover?", a: "All paid features for 14 days — team access, e-sign, white-label PDF, GSTR-1 export. No card needed; the plan auto-falls back to free unless you upgrade." },
  { q: "Can I cancel anytime?", a: "Yes. Cancel from Settings → Billing in one click. You keep access until the end of your billing cycle, then auto-revert to the free plan with all your data intact." },
  { q: "Do you offer discounts for CAs / accountants?", a: "Yes — CAs get 50% off the Growth plan and a free CA Workspace to manage all client books from one dashboard. Email us for the discount link." },
  { q: "Are payments via Razorpay secure?", a: "Yes. Razorpay is PCI-DSS Level 1 certified and used by 5M+ Indian businesses. We never see or store your card details." },
  { q: "Can I get an invoice for the subscription?", a: "Of course — every payment generates a GST-compliant invoice that auto-emails to you and is downloadable from Settings → Billing." },
];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PricingPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { plan: currentPlan, isTrial, trialDaysLeft, refreshPlan } = usePlan();
  const [processing, setProcessing] = useState(null);
  const { t, lang } = useT();
  const [pricing, setPricing] = useState(null);

  useEffect(() => {
    axios.get(`${API}/pricing/public`)
      .then((r) => setPricing(r.data))
      .catch(() => setPricing(null));
  }, []);

  const monthly = pricing?.monthly || { original: 499, discounted: 149, discount_months: 3, first_charge: 447, renewal: 499 };
  const yearly = pricing?.yearly || { original: 4999, discounted: 2999, renewal: 4999 };
  const offerBanner = pricing?.offer_active ? pricing.banner_text : "";

  const handleUpgrade = async (planId) => {
    if (!isAuthenticated) {
      navigate("/auth");
      return;
    }

    setProcessing(planId);
    try {
      const res = await axios.post(`${API}/subscription/upgrade`, { plan: planId }, { withCredentials: true });
      if (!res.data.success) throw new Error("Order creation failed");

      const configRes = await axios.get(`${API}/payment/config`);
      const rzpKey = configRes.data.key_id;

      if (!rzpKey || !window.Razorpay) {
        toast.error("Payment gateway not loaded. Please refresh.");
        setProcessing(null);
        return;
      }

      const isSubscription = res.data.mode === "subscription" && res.data.subscription_id;
      // Subscription mode triggers Razorpay's UPI AutoPay / eMandate flow — GPay/bank prompts
      // the user to authorise auto-debit. Order mode is one-time only (no autopay).
      const description = planId === "yearly"
        ? `Yearly Plan — ₹${(res.data.amount/100).toLocaleString("en-IN")} today, auto-renew at regular price next year`
        : `Growth Plan — ₹${(res.data.amount/100).toLocaleString("en-IN")} for first 3 months, then auto-renew monthly`;

      const options = {
        key: rzpKey,
        currency: "INR",
        name: "InvoiceSetu",
        description,
        theme: { color: "#7C3AED" },
        modal: { ondismiss: () => setProcessing(null) },
        handler: async function (response) {
          try {
            const verifyBody = isSubscription
              ? {
                  subscription_id: response.razorpay_subscription_id,
                  payment_id: response.razorpay_payment_id,
                  signature: response.razorpay_signature,
                }
              : {
                  order_id: response.razorpay_order_id,
                  payment_id: response.razorpay_payment_id,
                  signature: response.razorpay_signature,
                };
            const verify = await axios.post(`${API}/subscription/verify-payment`, verifyBody, { withCredentials: true });
            toast.success(
              verify.data?.auto_renew_scheduled
                ? "Plan upgraded! Auto-renew is set up — you'll see the mandate in your UPI app."
                : "Plan upgraded successfully!"
            );
            refreshPlan();
          } catch {
            toast.error("Payment verification failed");
          } finally {
            setProcessing(null);
          }
        },
      };

      if (isSubscription) {
        options.subscription_id = res.data.subscription_id;
        // Razorpay derives upfront `amount` from the subscription's addons + plan when
        // opening Checkout — we don't pass `amount` here.
      } else {
        options.order_id = res.data.order_id;
        options.amount = res.data.amount;
      }

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start payment");
      setProcessing(null);
    }
  };

  const plans = [
    {
      id: "free",
      name: "Free",
      tagline: "Get started with invoicing",
      price: 0,
      period: "forever",
      features: [
        "Unlimited invoice creation",
        "Send invoices via link",
        "Last 50 invoices accessible",
        "Basic GST calculation",
        "InvoiceSetu branding on invoices",
      ],
      color: "slate",
      current: isAuthenticated && currentPlan === "free" && !isTrial,
    },
    {
      id: "growth",
      name: "Growth",
      tagline: "For growing businesses",
      price: 149,                       // promotional price for first 3 months
      priceOriginal: 499,               // struck-through standard monthly
      introductoryMonths: 3,
      renewalPrice: 499,
      period: "month",
      popular: true,
      billingTimeline: "30-day free trial \u2192 \u20B9149/month for the first 3 months \u2192 \u20B9499/month after promotional period.",
      features: [
        "Everything in Free",
        "Full invoice history access",
        "GST-ready reports (GSTR-1, 3B)",
        "Business analytics dashboard",
        "CA/Professional collaboration",
        "Document workflows",
        "Remove InvoiceSetu branding",
        "Priority support",
      ],
      color: "violet",
      current: isAuthenticated && currentPlan === "growth",
    },
    {
      id: "yearly",
      name: "Yearly",
      tagline: "Best value for committed businesses",
      price: 2999,                      // promotional first-year price
      renewalPrice: 4999,               // auto-renewal price from year 2
      priceMonthly: 250,
      savings: 2989,
      period: "year",
      billingTimeline: "30-day free trial \u2192 \u20B92,999 for the first year \u2192 auto-renews at \u20B94,999/year.",
      features: [
        "Everything in Growth",
        "Save \u20B92,989 in first year",
        "\u20B9250/month equivalent",
        "Annual billing convenience",
      ],
      color: "blue",
      current: isAuthenticated && currentPlan === "yearly",
    },
  ];

  const benefits = [
    { icon: FileText, title: "Full Invoice History", desc: "Never lose your invoices. Access complete history anytime.", color: "bg-blue-50 text-blue-600" },
    { icon: Users, title: "CA Collaboration", desc: "No more WhatsApp follow-ups. Work seamlessly with your accountant.", color: "bg-violet-50 text-violet-600" },
    { icon: BarChart3, title: "GST Reports", desc: "Filing-ready reports instantly. GSTR-1, 3B, and HSN summaries.", color: "bg-emerald-50 text-emerald-600" },
    { icon: Shield, title: "Remove Branding", desc: "Look professional. Your invoices, your brand.", color: "bg-amber-50 text-amber-600" },
  ];

  const useCases = [
    { icon: Building2, title: "Small Business", desc: "Track invoices, manage payments, file GST. All in one place." },
    { icon: Briefcase, title: "Freelancer", desc: "Professional invoices, instant GST calculation, no branding." },
    { icon: Calculator, title: "CA-Managed Business", desc: "Your accountant manages everything. You focus on growth." },
  ];

  return (
    <div className="min-h-screen bg-[#F9FAFB] py-12 px-4" data-testid="pricing-page">
      <SEO
        title="Pricing — Free Plan + Paid Plans for Growing Businesses"
        description="Simple, transparent pricing. Free forever plan available. Pro plan starts at ₹149/mo. No credit card to start. CA discount 50%."
        path={lang === "en" ? "/pricing" : `/${lang}/pricing`}
        multilingual
        schemas={[
          ORGANIZATION_SCHEMA,
          buildBreadcrumbSchema([{ name: "Home", url: "/" }, { name: "Pricing", url: "/pricing" }]),
          buildFAQSchema(PRICING_FAQS),
        ]}
      />      <div className="max-w-6xl mx-auto">

        {/* Admin-configured offer banner */}
        {offerBanner && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5 mb-6 text-center" data-testid="offer-banner">
            <span className="text-sm font-semibold text-amber-800">{offerBanner}</span>
          </div>
        )}

        {/* Trial Banner */}
        {isTrial && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-r from-violet-600 to-blue-600 rounded-2xl p-5 mb-10 text-white text-center"
            data-testid="trial-banner"
          >
            <div className="flex items-center justify-center gap-2 mb-1">
              <Sparkles className="w-5 h-5" />
              <span className="text-lg font-bold">You're on a 30-day free trial</span>
            </div>
            <p className="text-sm text-white/80">{trialDaysLeft} days remaining. All Growth features are unlocked.</p>
          </motion.div>
        )}

        {/* Headline */}
        <div className="text-center mb-12">
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="pricing-headline">
            {t("pricing.headline")}
          </h1>
          <p className="text-base text-slate-500 max-w-xl mx-auto">
            {t("pricing.subhead")}
          </p>
          <p className="text-xs text-violet-600 font-semibold mt-3 bg-violet-50 px-4 py-1.5 rounded-full inline-block border border-violet-100">
            {t("pricing.introBadge")}
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-16" data-testid="pricing-cards">
          {plans.map((p, i) => (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className={`relative bg-white rounded-2xl border-2 p-6 ${
                p.popular
                  ? "border-violet-400 shadow-lg shadow-violet-100"
                  : "border-slate-100 shadow-sm"
              }`}
              data-testid={`plan-card-${p.id}`}
            >
              {p.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="bg-violet-600 text-white text-xs font-bold px-4 py-1 rounded-full shadow-md" data-testid="popular-badge">
                    Most Popular
                  </span>
                </div>
              )}

              {p.current && (
                <div className="absolute top-4 right-4">
                  <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200" data-testid="current-plan-badge">
                    Current Plan
                  </span>
                </div>
              )}

              <div className="mb-5">
                <h3 className="text-lg font-bold text-slate-900">{p.name}</h3>
                <p className="text-sm text-slate-500 mt-0.5">{p.tagline}</p>
              </div>

              {/* Price */}
              <div className="mb-5">
                {p.id === "free" ? (
                  <div>
                    <span className="text-4xl font-bold text-slate-900">Free</span>
                    <span className="text-sm text-slate-500 ml-1">forever</span>
                  </div>
                ) : p.id === "growth" ? (
                  <div>
                    <span className="text-sm text-slate-400 line-through mr-2" data-testid="growth-original-price">{"\u20B9"}{monthly.original}</span>
                    <span className="text-4xl font-bold text-slate-900" data-testid="growth-price">{"\u20B9"}{monthly.discounted}</span>
                    <span className="text-sm text-slate-500">/month</span>
                    <p className="text-xs text-violet-600 font-medium mt-1">Promo: first {monthly.discount_months} months</p>
                    <div className="mt-2 bg-violet-50 border border-violet-100 rounded-lg p-2.5" data-testid="growth-first-charge">
                      <p className="text-xs text-slate-700"><span className="font-bold text-violet-700">{"\u20B9"}{monthly.first_charge.toLocaleString("en-IN")} today</span> &mdash; covers next {monthly.discount_months} months ({monthly.discount_months} &times; {"\u20B9"}{monthly.discounted})</p>
                      <p className="text-[11px] text-slate-500 mt-0.5">Auto-renews at <span className="font-semibold">{"\u20B9"}{monthly.renewal}/month</span> from month {monthly.discount_months + 1}. Cancel anytime.</p>
                    </div>
                  </div>
                ) : (
                  <div>
                    <span className="text-sm text-slate-400 line-through mr-2" data-testid="yearly-original-price">{"\u20B9"}{yearly.original.toLocaleString("en-IN")}</span>
                    <span className="text-4xl font-bold text-slate-900" data-testid="yearly-price">{"\u20B9"}{yearly.discounted.toLocaleString("en-IN")}</span>
                    <span className="text-sm text-slate-500">/year</span>
                    <p className="text-xs text-blue-600 font-medium mt-1">Limited-time launch price</p>
                    <div className="mt-2 bg-blue-50 border border-blue-100 rounded-lg p-2.5" data-testid="yearly-first-charge">
                      <p className="text-xs text-slate-700"><span className="font-bold text-blue-700">{"\u20B9"}{yearly.discounted.toLocaleString("en-IN")} today</span> &mdash; covers first year</p>
                      <p className="text-[11px] text-slate-500 mt-0.5">Auto-renews at <span className="font-semibold">{"\u20B9"}{yearly.renewal.toLocaleString("en-IN")}/year</span> from year 2. Cancel anytime.</p>
                    </div>
                  </div>
                )}
              </div>

              {/* CTA */}
              {p.id === "free" ? (
                <Button
                  variant="outline"
                  className="w-full mb-5"
                  disabled={p.current}
                  data-testid="free-btn"
                >
                  {p.current ? "Current Plan" : "Get Started"}
                </Button>
              ) : (
                <Button
                  onClick={() => handleUpgrade(p.id)}
                  disabled={processing === p.id || p.current}
                  className={`w-full mb-5 ${
                    p.popular
                      ? "bg-violet-600 hover:bg-violet-700 text-white"
                      : "bg-blue-600 hover:bg-blue-700 text-white"
                  }`}
                  data-testid={`upgrade-${p.id}-btn`}
                >
                  {processing === p.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : p.current ? (
                    "Current Plan"
                  ) : (
                    <span className="flex items-center gap-2">
                      <Zap className="w-4 h-4" /> Upgrade to {p.name}
                    </span>
                  )}
                </Button>
              )}

              {/* Features */}
              <ul className="space-y-2.5">
                {p.features.map((f, j) => (
                  <li key={j} className="flex items-start gap-2.5 text-sm text-slate-700">
                    <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                      p.popular ? "text-violet-500" : p.color === "blue" ? "text-blue-500" : "text-slate-400"
                    }`} />
                    {f}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Benefit Cards */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-slate-900 text-center mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Why upgrade?
          </h2>
          <p className="text-sm text-slate-500 text-center mb-8">Everything you need to run your business professionally</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="benefit-cards">
            {benefits.map((b, i) => (
              <motion.div
                key={b.title}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 + i * 0.08 }}
                className="bg-white rounded-xl border border-slate-100 p-5 shadow-sm"
                data-testid={`benefit-${i}`}
              >
                <div className={`w-10 h-10 rounded-xl ${b.color} flex items-center justify-center mb-3`}>
                  <b.icon className="w-5 h-5" />
                </div>
                <h3 className="text-sm font-bold text-slate-900 mb-1">{b.title}</h3>
                <p className="text-xs text-slate-500 leading-relaxed">{b.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Use Cases */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-slate-900 text-center mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Built for every business
          </h2>
          <p className="text-sm text-slate-500 text-center mb-8">Whether you're just starting out or scaling up</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="use-cases">
            {useCases.map((uc, i) => (
              <div key={uc.title} className="bg-white rounded-xl border border-slate-100 p-5 shadow-sm" data-testid={`use-case-${i}`}>
                <uc.icon className="w-6 h-6 text-slate-400 mb-3" />
                <h3 className="text-sm font-bold text-slate-900 mb-1">{uc.title}</h3>
                <p className="text-xs text-slate-500 leading-relaxed">{uc.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CA / Professional Pricing — dedicated section on main pricing page */}
        <div className="mb-16 rounded-2xl bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 p-6 sm:p-10 text-white" data-testid="ca-pricing-block">
          <div className="text-center mb-8">
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-blue-200 mb-2">For Chartered Accountants &amp; Tax Professionals</p>
            <h2 className="text-2xl sm:text-3xl font-black mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              One flat price. Unlimited clients.
            </h2>
            <p className="text-blue-100 text-sm max-w-xl mx-auto">
              {pricing?.ca?.banner_text || "Built for practising CAs — no per-client licence fee, ever."}
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4 max-w-3xl mx-auto">
            <div className="bg-white text-slate-900 rounded-2xl p-6 shadow-xl" data-testid="pricing-ca-yearly">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-base font-bold">CA Yearly</h3>
                <Calculator className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-3xl font-black">₹{(pricing?.ca?.yearly ?? 6999).toLocaleString("en-IN")}</span>
                <span className="text-xs text-slate-500">/ year</span>
              </div>
              <p className="text-xs text-slate-500 mb-4">Unlimited client workspaces</p>
              <Button
                onClick={() => navigate("/for-ca")}
                variant="outline"
                className="w-full text-sm"
                data-testid="pricing-ca-yearly-cta"
              >
                See CA features
              </Button>
            </div>
            <div className="relative bg-yellow-400 text-slate-900 rounded-2xl p-6 shadow-xl" data-testid="pricing-ca-lifetime">
              <span className="absolute -top-2 right-4 bg-slate-900 text-yellow-300 text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full">Best value</span>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-base font-bold">CA Lifetime</h3>
                <Briefcase className="w-5 h-5" />
              </div>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-3xl font-black">₹{(pricing?.ca?.lifetime ?? 29999).toLocaleString("en-IN")}</span>
                <span className="text-xs text-slate-700">one-time</span>
              </div>
              <p className="text-xs text-slate-700 mb-4">Pay once. Use forever.</p>
              <Button
                onClick={() => navigate("/for-ca")}
                className="w-full text-sm bg-slate-900 text-white hover:bg-slate-800"
                data-testid="pricing-ca-lifetime-cta"
              >
                See CA features
              </Button>
            </div>
          </div>
        </div>

        {/* Conversion Line */}
        <div className="text-center bg-gradient-to-r from-violet-50 to-blue-50 rounded-2xl border border-violet-100 p-8" data-testid="conversion-cta">
          <p className="text-base font-semibold text-slate-900 mb-2">
            Most businesses upgrade once they start working with their accountant
          </p>
          <p className="text-sm text-slate-500 mb-5">
            Start free, grow with confidence. Upgrade when you need more control.
          </p>
          {!isAuthenticated ? (
            <Button onClick={() => navigate("/auth")} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="cta-signup">
              Get Started Free <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : currentPlan === "free" && !isTrial ? (
            <Button onClick={() => handleUpgrade("growth")} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="cta-upgrade">
              <Zap className="w-4 h-4 mr-2" /> Upgrade Now
            </Button>
          ) : null}
        </div>

        {/* FAQ — for SEO + AI search */}
        <div className="mt-16 max-w-3xl mx-auto" data-testid="pricing-faqs">
          <h2 className="text-2xl font-bold text-slate-900 text-center mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Pricing FAQs</h2>
          <p className="text-sm text-slate-500 text-center mb-6">Everything about plans, payments, and refunds.</p>
          <div className="space-y-2">
            {PRICING_FAQS.map((f, i) => <PricingFAQItem key={i} q={f.q} a={f.a} index={i} />)}
          </div>
        </div>
      </div>
    </div>
  );
}

function PricingFAQItem({ q, a, index }) {
  const [open, setOpen] = useState(index === 0);
  return (
    <div className="bg-white rounded-xl border border-slate-100" data-testid={`pricing-faq-${index}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full text-left px-5 py-4 flex items-center justify-between gap-3 hover:bg-slate-50/50 transition-colors"
        aria-expanded={open}
      >
        <span className="font-semibold text-slate-900 text-sm sm:text-base">{q}</span>
        <ChevronDown className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-5 pb-4 text-sm text-slate-600 leading-relaxed">{a}</div>}
    </div>
  );
}
